import type { BrowserBackend } from "../backend/contracts";
import { RUNTIME_CANDIDATES, type RuntimeCandidate } from "./candidates";

export type BenchmarkMarker = {
  name: string;
  status: "PASS" | "FAIL" | "NOT_RUN";
  durationMs?: number;
  detail?: string;
};

export type BenchmarkResult = {
  candidate: RuntimeCandidate;
  markers: BenchmarkMarker[];
  startedAt: string;
};

export async function benchmarkNative(backend: BrowserBackend): Promise<BenchmarkResult> {
  const candidate = RUNTIME_CANDIDATES[0];
  const markers: BenchmarkMarker[] = [];
  const run = async (name: string, operation: () => Promise<void>) => {
    const started = performance.now();
    try {
      await operation();
      markers.push({ name, status: "PASS", durationMs: Math.round(performance.now() - started) });
    } catch (error) {
      markers.push({
        name,
        status: "FAIL",
        durationMs: Math.round(performance.now() - started),
        detail: error instanceof Error ? error.message : String(error),
      });
    }
  };

  await run("BOOT_PASS", () => backend.start());
  await run("HEALTH_PASS", async () => {
    const response = await backend.request(new Request(`${window.location.origin}/api/health`));
    if (!response.ok) throw new Error(`health returned ${response.status}`);
  });
  await run("HTTP_ROUTE_PASS", async () => {
    const response = await backend.request(new Request(`${window.location.origin}/api/items`));
    if (!response.ok) throw new Error(`items returned ${response.status}`);
  });
  await run("PERSISTENCE_PASS", async () => {
    const snapshot = await backend.snapshot();
    if (snapshot.byteLength === 0) throw new Error("snapshot was empty");
  });

  return { candidate, markers, startedAt: new Date().toISOString() };
}

export function optionalBenchmarkResults(): BenchmarkResult[] {
  return RUNTIME_CANDIDATES.filter((candidate) => candidate.status === "optional-benchmark").map((candidate) => ({
    candidate,
    startedAt: new Date().toISOString(),
    markers: [{
      name: "OPTIONAL_RUNTIME_NOT_LOADED",
      status: "NOT_RUN",
      detail: "Candidate is isolated from the baseline bundle; run the candidate benchmark command before selecting it.",
    }],
  }));
}
